import { useState } from 'react';
import { ValueMap, ChatMessage } from './types';
import { api } from './services/api';
import { exportValueMapAsPDF, exportValueMapAsPNG } from './services/export';
import Header from './components/layout/Header';
import PastePanel from './components/paste/PastePanel';
import ChatPanel from './components/chat/ChatPanel';
import ValueMapPanel from './components/valuemap/ValueMapPanel';

export default function App() {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [valueMap, setValueMap] = useState<ValueMap | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showChat, setShowChat] = useState(false);

  const handleParseSummary = async (executiveSummary: string, mode: 'simple' | 'advanced' = 'simple') => {
    setLoading(true);
    setError(null);

    const result = await api.parseSummary(executiveSummary, mode);

    if (result.success && result.data) {
      setSessionId(result.data.sessionId);
      setValueMap(result.data.valueMap);
      setShowChat(true);
    } else {
      setError(result.error || 'Failed to parse summary');
    }

    setLoading(false);
  };

  const handleSendMessage = async (message: string) => {
    if (!sessionId || !valueMap) return;

    // Add user message immediately
    const userMessage: ChatMessage = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    };
    setChatMessages((prev) => [...prev, userMessage]);

    setLoading(true);
    const result = await api.refineValueMap(sessionId, message, valueMap);

    if (result.success && result.data) {
      // Add assistant response
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: result.data.response,
        timestamp: new Date().toISOString(),
      };
      setChatMessages((prev) => [...prev, assistantMessage]);

      // Update value map
      setValueMap(result.data.updatedValueMap);
    } else {
      setError(result.error || 'Failed to refine value map');
    }

    setLoading(false);
  };

  const handleExportPDF = async () => {
    if (!valueMap || !sessionId) return;

    try {
      // Extract company name if available (for advanced mode)
      const companyName = 'customerName' in valueMap ? valueMap.customerName : undefined;
      await exportValueMapAsPDF(valueMap, companyName);
      await api.trackExport(sessionId, 'pdf');
    } catch (error: any) {
      setError(error.message || 'Failed to export PDF');
    }
  };

  const handleExportPNG = async () => {
    if (!valueMap || !sessionId) return;

    try {
      // Extract company name if available (for advanced mode)
      const companyName = 'customerName' in valueMap ? valueMap.customerName : undefined;
      await exportValueMapAsPNG(valueMap, companyName);
      await api.trackExport(sessionId, 'png');
    } catch (error: any) {
      setError(error.message || 'Failed to export PNG');
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <Header />

      <div className="flex-1 grid grid-cols-2 gap-0 overflow-hidden">
        {/* LEFT PANEL */}
        <div className="bg-white border-r border-gray-200 flex flex-col overflow-hidden">
          {!showChat ? (
            <PastePanel onParse={handleParseSummary} loading={loading} />
          ) : (
            <ChatPanel
              messages={chatMessages}
              onSendMessage={handleSendMessage}
              loading={loading}
            />
          )}
        </div>

        {/* RIGHT PANEL */}
        <div className="bg-gray-50 flex flex-col overflow-hidden">
          <ValueMapPanel
            valueMap={valueMap}
            loading={loading}
            error={error}
            onExportPDF={handleExportPDF}
            onExportPNG={handleExportPNG}
          />
        </div>
      </div>
    </div>
  );
}
